import React, { useState, useEffect } from 'react';
import { Brain, Building2, Target, Zap, MousePointerClick, Shield } from 'lucide-react';
import { benefits } from '../../data/mock';

const iconMap = {
  Brain: Brain,
  Building2: Building2,
  Target: Target,
  Zap: Zap,
  MousePointerClick: MousePointerClick,
  Shield: Shield
};

// Parallax hook
const useParallax = () => {
  const [scrollY, setScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return scrollY;
};

const BenefitsSection = () => {
  const scrollY = useParallax();
  
  return (
    <section className="py-24 bg-white relative overflow-hidden">
      {/* Parallax Decorative Elements - MORE VISIBLE */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Circles */}
        <div 
          className="absolute w-56 h-56 rounded-full border-3 border-[#FF0000]/20 hidden lg:block"
          style={{ 
            top: '5%', 
            right: '3%',
            transform: `translateY(${(scrollY - 600) * 0.2}px)` 
          }}
        />
        <div 
          className="absolute w-32 h-32 rounded-full bg-[#FFE5E5]/50 hidden lg:block"
          style={{ 
            bottom: '10%', 
            left: '5%',
            transform: `translateY(${(scrollY - 600) * -0.15}px)` 
          }}
        />
        <div 
          className="absolute w-20 h-20 rounded-full bg-[#FF0000]/10 hidden lg:block"
          style={{ 
            top: '40%', 
            left: '2%',
            transform: `translateY(${(scrollY - 600) * 0.12}px)` 
          }}
        />
        
        {/* Arrows */}
        <img 
          src="/arrow-right.webp" 
          alt="" 
          className="absolute w-36 h-auto opacity-25 hidden lg:block"
          style={{ 
            top: '15%', 
            left: '-3%',
            transform: `translateY(${(scrollY - 600) * 0.22}px)` 
          }}
          aria-hidden="true"
        />
        <img 
          src="/arrow-down.webp" 
          alt="" 
          className="absolute w-24 h-auto opacity-20 hidden xl:block"
          style={{ 
            bottom: '20%', 
            right: '5%',
            transform: `translateY(${(scrollY - 600) * -0.18}px)` 
          }}
          aria-hidden="true"
        />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="caption mb-4 block">Warum unternehmenbewerten.ch</span>
          <h2 className="heading-2 mb-6">
            Warum unseren <span className="text-[#FF0000]">Unternehmenswert-Rechner</span> nutzen?
          </h2>
          <p className="body-medium max-w-4xl mx-auto text-[#5a5d5b] leading-relaxed">
            Moderne KI-Technologie kombiniert mit Schweizer Präzision – unternehmenbewerten.ch ist ein 
            Produkt der Schweizer Nachfolge Experten AG. Als eine der etablierten Beratungsgesellschaften 
            für Unternehmensbewertungen, Unternehmensnachfolge sowie den Kauf und Verkauf von Schweizer KMU 
            verfügt die Schweizer Nachfolge Experten AG über langjährige Erfahrung aus zahlreichen 
            Bewertungs- und Transaktionsmandaten. Mit einem Team aus erfahrenen M&A-Beratern und fundiertem 
            Marktverständnis verbinden wir technologische Innovation mit praktischer Umsetzungskompetenz.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => {
            const IconComponent = iconMap[benefit.icon];
            return (
              <div
                key={benefit.title}
                className="group p-8 rounded-2xl bg-[#f8f9fa] border border-gray-100 hover:border-[#FF0000]/30 hover:shadow-lg transition-all duration-300 card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-[#FFE5E5] flex items-center justify-center mb-6 group-hover:bg-[#FF0000]/20 transition-colors">
                  {IconComponent && <IconComponent className="w-7 h-7 text-[#FF0000]" />}
                </div>
                <h3 className="heading-3 mb-3">{benefit.title}</h3>
                <p className="body-medium">{benefit.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
